import { msalInstance } from "../.";
import { getIdMeBaseApiUrl } from "./UrlConstants";

type FetchResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  ok: boolean;
  headers: Headers;
  json: () => Promise<T>;
  text: () => Promise<string>;
};

class FetchHttpError<T = any> extends Error {
  response: FetchResponse<T>;
  constructor(message: string, response: FetchResponse<T>) {
    super(message);
    this.response = response;
  }
}

const baseUrl = getIdMeBaseApiUrl();
const defaultHeaders: Record<string, string> = {
  "Content-Type": "application/json",
  "Accept": "*/*",
};

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(function (cb) { cb(token); });
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const buildUrl = (url: string) => {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return baseUrl + url;
};

const parseBody = async (response: Response) => {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.toLowerCase().indexOf("application/json") >= 0) {
    return response.json();
  }
  return response.text();
};

const getCurrentToken = () => {
  return sessionStorage.getItem("msalToken") || sessionStorage.getItem("userToken");
};

const refreshAccessToken = async (): Promise<string> => {
  const msalToken = sessionStorage.getItem("msalToken");

  if (msalToken && sessionStorage.getItem('internal_token')) {
    const account = msalInstance.getAllAccounts()[0];
    const response = await msalInstance.acquireTokenSilent({
      account: account,
      scopes: ["User.Read", "Group.Read.All"],
    });
    const accessToken = response.accessToken;
    sessionStorage.setItem("msalToken", accessToken);
    return accessToken;
  }

  if (msalToken && sessionStorage.getItem('external_token')) {
    const res = await fetch(buildUrl("/auth/b2c/refresh-token"), {
      method: "POST",
      headers: defaultHeaders,
    });
    if (!res.ok) throw new Error("Failed to refresh b2c token");
    const data = await res.json();
    sessionStorage.setItem("msalToken", data.access_token);
    return data.access_token;
  }

  throw new Error("No token available for refresh");
};

export const apiFetch = async <T = any>(
  url: string,
  init: RequestInit = {},
  retryCount = 0
): Promise<FetchResponse<T>> => {
  const token = getCurrentToken();

  const mergedHeaders: Record<string, string> = {
    ...defaultHeaders,
    ...(init.headers as Record<string, string>),
  };

  if (token) mergedHeaders.Authorization = "Bearer " + token;
  else delete mergedHeaders.Authorization;

  const response = await fetch(buildUrl(url), {
    ...init,
    headers: mergedHeaders,
  });

  if (response.status === 401 && retryCount < 2) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        onRefreshed(newToken);
        return apiFetch<T>(url, init, retryCount + 1);
      } catch (err) {
        isRefreshing = false;
        throw err;
      }
    }

    return new Promise<FetchResponse<T>>((resolve, reject) => {
      addRefreshSubscriber(async function () {
        try {
          const retried = await apiFetch<T>(url, init, retryCount + 1);
          resolve(retried);
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  const data = await parseBody(response);
  const shaped: FetchResponse<T> = {
    data: data as T,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    headers: response.headers,
    json: () => Promise.resolve(data as T),
    text: () => Promise.resolve(typeof data === "string" ? data : JSON.stringify(data)),
  };

  // if (!response.ok) {
  //   throw new FetchHttpError("Request failed", shaped);
  // }

  return shaped;
};